import { useQueries } from "@tanstack/react-query";
import { eachMonthOfInterval, endOfYear, formatISO, parseISO, startOfYear } from "date-fns";
import { useTranslation } from "react-i18next";
import { useDateFormatter } from "../../../i18n/useDateFormatter";
import { DevErrorDisplay } from "../../../components/core/blocks/DevErrorDisplay";
import Loading from "../../../components/core/blocks/Loading";
import { useOrgGoalsQueryOptions } from "../api/useOrgGoalsQueryOptions";
import {
  useGetV4CategoryReportByDate,
  type CategoryReportByDateRow,
} from "../../perf_reports/api/category/v4CategoryReportByDate";
import { Table } from "../../../components/table/Table";
import { useClientTable } from "../../../components/table/tableFeatures";
import { createColumnBuilder } from "../../../components/table/columnBuilder";
import type { OrgGoal } from "../../../zodios/apiDefinitions/goals";
import { formatCurrency, formatPercent } from "../../../utils/formatNumber";
import { useAppSelector } from "../../../redux/hooks";
import { Button } from "../../../components/core/basic/Button";
import useQueryParams from "../../../hooks/useQueryParams";
import { useMemo } from "react";

const columnHelper = createColumnBuilder<{
  start_date: string;
  stats: CategoryReportByDateRow | null;
  goal: OrgGoal | null;
}>();

export function OverviewTab({
  displayPeriod,
  setDisplayPeriod,
}: {
  displayPeriod: Date;
  setDisplayPeriod: React.Dispatch<React.SetStateAction<Date>>;
}) {
  const { t } = useTranslation(["goals"]);
  const monthYearFmt = useDateFormatter("monthYearLong");
  const startDate = startOfYear(displayPeriod);
  const endDate = endOfYear(displayPeriod);
  const [, setTab] = useQueryParams("tab", "details");

  const goalOverviewQueries = useQueries({
    queries: [
      useOrgGoalsQueryOptions(
        formatISO(startDate, {
          representation: "date",
        }),
        formatISO(endDate, {
          representation: "date",
        }),
      ),
      useGetV4CategoryReportByDate()({
        start: startDate,
        end: endDate,
        preStart: startDate,
        preEnd: endDate,
        group: "month",
      }),
    ],
    combine: (queries) => {
      const orgGoals = queries[0].data;
      const categoryReport = queries[1].data;

      const data = eachMonthOfInterval({ start: startDate, end: endDate }).map((month) => {
        const isoMonth = formatISO(month, { representation: "date" });
        const monthStats = categoryReport?.data.find((item) => item.start_date === isoMonth);
        const monthGoal = orgGoals?.find((item) => item.start_date === isoMonth);

        return {
          start_date: isoMonth,
          stats: monthStats ?? null,
          goal: monthGoal ?? null,
        };
      });

      return {
        data,
        isLoading: queries.some((result) => result.isLoading),
        isError: queries.some((result) => result.isError),
        isSuccess: queries.every((result) => result.isSuccess),
        error: queries.find((result) => result.isError)?.error,
      };
    },
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor("start_date", {
        id: "start_date",
        header: t(($) => $.goals["Period"]),
        cell: ({ getValue }) => (
          <Button
            variant="link"
            onPress={() => {
              setDisplayPeriod(parseISO(getValue()));
              setTab("details");
            }}
          >
            {monthYearFmt.format(parseISO(getValue()))}
          </Button>
        ),
      }),

      columnHelper.accessor((row) => row.stats?.total_sales, {
        id: "total_sales",
        header: t(($) => $.goals["Total Sales"]),
        cell: function Cell({ getValue }) {
          const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
          return formatCurrency(getValue(), {
            marketPlace: marketPlace,
            decimalPlaces: 0,
          });
        },
        meta: {
          justify: "end",
        },
      }),
      columnHelper.accessor((row) => row.goal?.total_sales_goal, {
        id: "total_sales_goal",
        header: t(($) => $.goals["Sales Goal"]),
        cell: function Cell({ getValue }) {
          const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
          return formatCurrency(getValue(), {
            marketPlace: marketPlace,
            decimalPlaces: 0,
          });
        },
        meta: {
          justify: "end",
        },
      }),

      columnHelper.accessor((row) => row.stats?.cost, {
        id: "ad_spend",
        header: t(($) => $.goals["Ad Spend"]),
        cell: function Cell({ getValue }) {
          const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
          return formatCurrency(getValue(), {
            marketPlace: marketPlace,
            decimalPlaces: 0,
          });
        },
        meta: {
          justify: "end",
        },
      }),
      columnHelper.accessor((row) => row.goal?.ad_spend_goal, {
        id: "ad_spend_goal",
        header: t(($) => $.goals["Ad Spend Budget"]),
        cell: function Cell({ getValue }) {
          const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
          return formatCurrency(getValue(), {
            marketPlace: marketPlace,
            decimalPlaces: 0,
          });
        },
        meta: {
          justify: "end",
        },
      }),

      columnHelper.accessor((row) => row.stats?.total_acos, {
        id: "tacos",
        header: t(($) => $.goals["TACoS"]),
        cell: function Cell({ getValue }) {
          return formatPercent(getValue());
        },
        meta: {
          justify: "end",
        },
      }),
      columnHelper.accessor((row) => (row.goal ? row.goal.ad_spend_goal / row.goal.total_sales_goal : null), {
        id: "tacos_goal",
        header: t(($) => $.goals["TACoS Goal"]),
        cell: function Cell({ getValue }) {
          return formatPercent(getValue());
        },
        meta: {
          justify: "end",
        },
      }),
    ],
    [t, monthYearFmt, setDisplayPeriod, setTab],
  );

  const table = useClientTable({
    data: goalOverviewQueries.data,
    columns,
    enableSorting: false,
  });

  if (goalOverviewQueries.isLoading) {
    return <Loading />;
  }

  if (goalOverviewQueries.isError) {
    return (
      <DevErrorDisplay error={goalOverviewQueries.error} userMessage={t(($) => $.goals["Error loading overview"])} />
    );
  }

  return <Table table={table} />;
}
